import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeysCompany } from '~/data/company/queryKeys';

import { queryKeysCompanyAccount } from '../queryKeys';

import { deleteCompanyAccount } from './deleteCompanyAccount';
import {
  type DeleteCompanyAccountMutationOptions,
  type DeleteCompanyAccountVariables,
} from './types';

/**
 * Deletes a company account and clears related company-account and company data.
 *
 * @param options - Additional TanStack Query mutation options.
 * @returns The delete-company-account mutation result.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company-Account/operation/delete_company_account_vadmin_company_accounts__company_account_id__delete
 */
export function useMutationDeleteCompanyAccount(
  options?: DeleteCompanyAccountMutationOptions,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ companyAccountId }: DeleteCompanyAccountVariables) =>
      deleteCompanyAccount(companyAccountId),
    ...options,
    onSettled(data, error, variables, onMutateResult, context) {
      return options?.onSettled?.(
        data,
        error,
        variables,
        onMutateResult,
        context,
      );
    },
    async onSuccess(data, variables, onMutateResult, context) {
      queryClient.removeQueries({
        exact: true,
        queryKey: queryKeysCompanyAccount.get(variables.companyAccountId),
      });
      queryClient.removeQueries({
        exact: true,
        queryKey: queryKeysCompanyAccount.getDeletionCheck({
          companyAccountId: variables.companyAccountId,
        }),
      });

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [queryKeysCompanyAccount.base()],
          refetchType: 'none',
        }),
        queryClient.invalidateQueries({
          queryKey: [queryKeysCompany.base()],
          refetchType: 'none',
        }),
      ]);

      return options?.onSuccess?.(data, variables, onMutateResult, context);
    },
    retry: false,
  });
}
